// =============================================================================
// Email Express — right-hand block edit panel (Task #751)
// -----------------------------------------------------------------------------
// Renders the editable CONTENT fields for the currently-selected polished block
// (driven by the block→copy field mapping in shared/schema). Editing here writes
// to the same draft copy state the editor already autosaves — it never kicks off
// a render. Each block carries a clear state (Polished / Pending changes /
// Regenerating); a per-block "Regenerate" re-runs Stage 2 for just this block,
// enabled only while it has pending changes. The center polished preview stays
// visible the whole time this panel is open.
// =============================================================================
import { useState, useEffect, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { cn } from "@/lib/utils";
import type {
  EmailInstanceCopy,
  EmailBodyImagePreference,
  EmailImageTreatment,
  UniversalFooterContent,
  UniversalSocialLink,
  EmailSectionRegenScope,
} from "@shared/schema";
import {
  emailBlockType,
  EMAIL_SECTION_REGEN_SCOPES,
  EMAIL_IMAGE_TREATMENT_ELIGIBLE_TYPES,
} from "@shared/schema";
import {
  RefreshCw,
  CheckCircle2,
  Loader2,
  Plus,
  Trash2,
  Lock,
  Unlock,
  AlertCircle,
  PanelRightClose,
  Star,
  Check,
  Eye,
  EyeOff,
  Upload,
} from "lucide-react";

export type BlockEditState =
  | "polished"
  | "pending"
  | "regenerating"
  | "failed";

export interface EmailBlockEditPanelProps {
  blockKey: string;
  label: string;
  state: BlockEditState;
  // When state === "failed", the reason the last per-block regenerate failed.
  errorMessage?: string;
  // Whether a queued partial re-render is possible for this block (a per-block
  // manifest with carried images exists). When false the email predates the
  // per-block pipeline and edits fall back to the full re-polish on the next
  // explicit action.
  canRegenerate: boolean;
  // Email-level copy (hero headline / cta, body copy) + the resolved per-instance
  // copy for this block.
  headline: string;
  ctaText: string;
  bodyCopy: string;
  copy: EmailInstanceCopy;
  punchline: string;
  onHeadline: (v: string) => void;
  onCtaText: (v: string) => void;
  onBodyCopy: (v: string) => void;
  onPunchline: (v: string) => void;
  onPatch: (patch: Partial<EmailInstanceCopy>) => void;
  // Primary per-block regenerate ("Apply changes" / "Retry this section").
  // Carries the panel's optional feedback text (Task #974) so the targeted
  // re-render can be steered; blank/absent = unchanged behavior.
  onRegenerate: (feedback?: string) => void;
  onClose: () => void;
  // Real product reviews the user can FEATURE in the reviews block — the same
  // picker concept as Landing Page Express. Empty/absent hides the picker (the
  // free-text editor still lets the user hand-write reviews).
  availableReviews?: { quote: string; author: string; rating: number }[];
  // In-canvas footer editing surfaced in the panel (the polished view's only
  // editable surface — the live inline footer editor is parked off-screen there).
  // `content` is the EFFECTIVE footer (snapshot + overrides); each callback
  // persists a per-email override exactly like the live preview's inline footer
  // editor. The unsubscribe URL is editable but never deletable (compliance).
  footer?: {
    content: UniversalFooterContent | null;
    onSetSocialLinks: (links: UniversalSocialLink[]) => void;
    onSetDisclaimer: (text: string) => void;
    onDeleteDisclaimer: () => void;
    onSetUnsubscribeUrl: (url: string) => void;
    onSetPreferencesLabel: (label: string) => void;
    onSetPreferencesUrl: (url: string) => void;
    onDeletePreferences: () => void;
    onSetContactLabel: (label: string) => void;
    onSetContactUrl: (url: string) => void;
    onDeleteContact: () => void;
  };
  // ---- Task #863: scoped section regeneration + locking --------------------
  // Whether this section is explicitly locked (preserved by every regenerate).
  isLocked?: boolean;
  // Whether this section carries hand-edited copy (preserved by "Regenerate
  // content" / section regen until the user clears it).
  isEdited?: boolean;
  lockBusy?: boolean;
  clearBusy?: boolean;
  // A scoped "Regenerate this section" run is in flight for this section.
  sectionBusy?: boolean;
  onToggleLock?: (locked: boolean) => void;
  onClearEdits?: () => void;
  // Scoped section regeneration: copy | layout | image | full.
  onRegenerateSection?: (
    scope: EmailSectionRegenScope,
    feedback?: string,
  ) => void;
  // ---- Task #1914: per-CTA link override -----------------------------------
  // Present only for CTA-bearing blocks (hero/body). The field is prefilled
  // with the email's default CTA link; typing a different URL saves it as a
  // per-block override, clearing it falls back to the default.
  ctaLink?: {
    defaultUrl: string;
    overrideUrl: string;
    onChange: (url: string | null) => void;
  };
  // ---- Task #2286: per-instance image picker (image_only / image_discount) --
  // These blocks show REAL photos only (no AI regen). Present only for image
  // block types; renders the choose/upload/reset picker inside the panel so
  // the image is swappable even while the polished stack (not the live
  // preview) is on screen. Choosing/uploading is a plain instance-copy patch —
  // it never triggers polish or regeneration.
  blockImage?: {
    photos: string[];
    currentUrl: string;
    hasSelection: boolean;
    busy: boolean;
    onUpload: (file: File) => void;
    onChoose: (url: string) => void;
    onReset: () => void;
  };
  // ---- Task #2329: hero image picker ---------------------------------------
  // Same discoverable upload/choose UX as image blocks, plus the hero's AI
  // regenerate. Present only for the hero block. Choose/upload go through the
  // /hero/set route (durable URL becomes the canonical heroImageUrl); on a
  // polished email the editor re-polishes the hero section after a swap so the
  // visible composite reflects the new image.
  heroImage?: {
    photos: string[];
    currentUrl: string;
    /** True while a set/upload is in flight. */
    busy: boolean;
    /** True while an AI hero render is pending. */
    regenBusy: boolean;
    onUpload: (file: File) => void;
    onChoose: (url: string) => void;
    /** Re-roll the AI hero with optional art-direction feedback. */
    onRegenerate: (feedback: string) => void;
    /**
     * True when a swap landed but the polished hero hasn't been re-rendered —
     * per user request nothing regenerates automatically; the panel shows an
     * explicit "Apply new image" button instead.
     */
    needsApply: boolean;
    /** True while the apply (hero re-polish) is running. */
    applyBusy: boolean;
    onApply: () => void;
  };
  // Explicit Body Copy image treatment. Choosing a product photo, upload,
  // text-only, or automatic only stages and autosaves the preference; a
  // polished section changes only after the user deliberately applies it.
  bodyImage?: {
    photos: string[];
    preference: EmailBodyImagePreference;
    /** True only when the currently rendered Body contains replaceable imagery. */
    hasImage: boolean;
    /** True while a legacy rendered Body is being inspected on demand. */
    checkingImage?: boolean;
    busy: boolean;
    needsApply: boolean;
    applyBusy: boolean;
    onUpload: (file: File) => void;
    onChoose: (url: string) => void;
    onRemove: () => void;
    onRestoreAutomatic: () => void;
    onApply: () => void;
  };
  // ---- Task #2556: per-slot image treatment for eligible body-copy blocks ----
  // Present for selling_points, did_you_know, us_vs_them, comparison_table,
  // product_list when their current layout variant is image-bearing. For
  // singular blocks, `slots` has one entry keyed "main". For product_list, one
  // entry per visible tile keyed "tile_N". Hidden (undefined) when:
  //  • the block type is not in EMAIL_IMAGE_TREATMENT_ELIGIBLE_TYPES
  //  • no polished render exists (hasImage false for all slots)
  //  • the current layout variant is image-free
  // Fail-closed: controls only appear when verified image-bearing.
  blockBodyImage?: {
    /** Ordered list of slots this block exposes. */
    slots: Array<{
      /** "main" for singular blocks; "tile_N" for product_list. */
      slot: string;
      /** Human-readable label shown above the controls ("Image" / "Tile 1 image"). */
      label: string;
      /** Current image treatment staged for this slot. */
      treatment: EmailImageTreatment;
      /**
       * True when the current polished render for this slot is known to contain
       * imagery that can be replaced. Controls are hidden for slots without
       * verified imagery — fails closed for image-free layouts.
       */
      hasImage: boolean;
      /**
       * The URL the slot currently shows in the polished render (the AI-generated
       * image URL), or "" when none. Shown as the "current" thumbnail.
       */
      currentUrl: string;
      /**
       * Whether this slot's renderer supports omitting the image (mode:"none").
       * Singular blocks (selling_points/did_you_know/us_vs_them/comparison_table)
       * support removal; product_list tiles do NOT — their renderer always draws
       * a real product photo, so Remove is hidden/disabled for tiles.
       */
      allowRemove: boolean;
    }>;
    /** Available product photos for this email. */
    photos: string[];
    /** True while an upload is in flight for any slot. */
    busy: boolean;
    /** Which slot (if any) is currently uploading. */
    uploadingSlot?: string | null;
    /**
     * True when any slot has a staged (non-automatic) treatment that hasn't
     * been applied yet. When true the panel shows an Apply banner.
     */
    needsApply: boolean;
    applyBusy: boolean;
    onUpload: (slot: string, file: File) => void;
    onChoose: (slot: string, url: string) => void;
    onRemove: (slot: string) => void;
    onRestoreAutomatic: (slot: string) => void;
    onApply: () => void;
  };
}

// ---- Small field primitives ------------------------------------------------

function Field({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="space-y-1.5">
      <label className="text-xs font-semibold text-foreground">{label}</label>
      {hint && <p className="text-[11px] text-muted-foreground">{hint}</p>}
      {children}
    </div>
  );
}

function TextField({
  label,
  hint,
  value,
  onChange,
  placeholder,
  testid,
}: {
  label: string;
  hint?: string;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  testid: string;
}) {
  return (
    <Field label={label} hint={hint}>
      <Input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="h-8 text-sm"
        data-testid={testid}
      />
    </Field>
  );
}

function AreaField({
  label,
  hint,
  value,
  onChange,
  placeholder,
  testid,
  rows = 4,
}: {
  label: string;
  hint?: string;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  testid: string;
  rows?: number;
}) {
  return (
    <Field label={label} hint={hint}>
      <Textarea
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        rows={rows}
        className="text-sm resize-y"
        data-testid={testid}
      />
    </Field>
  );
}

// Editable list of plain strings (selling points / bullets).
function StringListField({
  label,
  hint,
  items,
  onChange,
  placeholder,
  addLabel,
  testid,
}: {
  label: string;
  hint?: string;
  items: string[];
  onChange: (next: string[]) => void;
  placeholder?: string;
  addLabel: string;
  testid: string;
}) {
  const set = (i: number, v: string) =>
    onChange(items.map((x, idx) => (idx === i ? v : x)));
  const remove = (i: number) => onChange(items.filter((_, idx) => idx !== i));
  const add = () => onChange([...items, ""]);
  return (
    <Field label={label} hint={hint}>
      <div className="space-y-1.5">
        {items.map((it, i) => (
          <div key={i} className="flex items-center gap-1.5">
            <Input
              value={it}
              onChange={(e) => set(i, e.target.value)}
              placeholder={placeholder}
              className="h-8 text-sm"
              data-testid={`${testid}-item-${i}`}
            />
            <button
              type="button"
              onClick={() => remove(i)}
              className="text-muted-foreground/60 hover:text-destructive transition-colors shrink-0"
              title="Remove"
              data-testid={`${testid}-remove-${i}`}
            >
              <Trash2 className="h-3.5 w-3.5" />
            </button>
          </div>
        ))}
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="h-7 gap-1.5 text-xs"
          onClick={add}
          data-testid={`${testid}-add`}
        >
          <Plus className="h-3 w-3" /> {addLabel}
        </Button>
      </div>
    </Field>
  );
}

// Per-CTA link override field (Task #1914). Shows the override when set, else
// the email's default CTA link. Typing a URL that differs from the default
// saves it as this block's override; clearing (or typing the default back)
// removes the override so the block follows the default again — including
// future default changes.
function CtaLinkField({
  defaultUrl,
  overrideUrl,
  onChange,
}: {
  defaultUrl: string;
  overrideUrl: string;
  onChange: (url: string | null) => void;
}) {
  const [val, setVal] = useState(overrideUrl || defaultUrl);
  const [focused, setFocused] = useState(false);
  // While not editing, track external changes (default edited in the left
  // sidebar, override cleared elsewhere) so the field never shows stale text.
  useEffect(() => {
    if (!focused) setVal(overrideUrl || defaultUrl);
  }, [overrideUrl, defaultUrl, focused]);
  const commit = (v: string) => {
    const t = v.trim();
    onChange(!t || t === defaultUrl.trim() ? null : t);
  };
  const isOverridden = !!overrideUrl && overrideUrl !== defaultUrl.trim();
  return (
    <Field
      label="CTA link"
      hint={
        isOverridden
          ? "This button uses its own link. Clear it to follow the email's default link again."
          : "Prefilled with the email's default link — change it to override just this button."
      }
    >
      <Input
        value={val}
        onChange={(e) => {
          setVal(e.target.value);
          commit(e.target.value);
        }}
        onFocus={() => setFocused(true)}
        onBlur={() => {
          setFocused(false);
          setVal(overrideUrl || defaultUrl);
        }}
        placeholder={defaultUrl || "yourbrand.com/sale"}
        className="h-8 text-sm"
        data-testid="input-block-cta-link"
      />
    </Field>
  );
}

// ---- Task #2286: panel image picker (image_only / image_discount) ----------
// Current image thumbnail + product-photo grid + upload/reset. Pure
// instance-copy patches — never triggers polish or regeneration.
function BlockImagePicker({
  image,
}: {
  image: NonNullable<EmailBlockEditPanelProps["blockImage"]>;
}) {
  const fileRef = useRef<HTMLInputElement>(null);
  return (
    <div className="space-y-2" data-testid="panel-block-image-picker">
      <label className="text-xs font-semibold text-foreground">Image</label>
      {image.currentUrl ? (
        <img
          src={image.currentUrl}
          alt="Current block image"
          className="max-h-40 w-full rounded-md border border-border object-cover"
          data-testid="img-panel-block-image-current"
        />
      ) : (
        <div className="rounded-md border border-dashed border-border/60 p-3 text-center text-[11px] text-muted-foreground">
          No image yet — choose or upload one.
        </div>
      )}
      {image.photos.length > 0 && (
        <>
          <p className="text-[11px] text-muted-foreground">Product photos</p>
          <div className="grid max-h-48 grid-cols-3 gap-1.5 overflow-y-auto">
            {image.photos.map((url, i) => (
              <button
                key={`${i}-${url}`}
                type="button"
                disabled={image.busy}
                onClick={() => image.onChoose(url)}
                title="Use this product photo"
                data-testid={`button-panel-block-image-option-${i}`}
                className={cn(
                  "relative aspect-square overflow-hidden rounded-md border bg-muted hover:border-primary disabled:opacity-40",
                  url === image.currentUrl
                    ? "border-primary ring-1 ring-primary"
                    : "border-border",
                )}
              >
                <img
                  src={url}
                  alt={`Product photo ${i + 1}`}
                  className="h-full w-full object-cover"
                />
              </button>
            ))}
          </div>
        </>
      )}
      <input
        ref={fileRef}
        type="file"
        accept="image/png,image/jpeg,image/webp,image/gif"
        className="hidden"
        data-testid="input-panel-upload-block-image"
        onChange={(e) => {
          const file = e.target.files?.[0];
          e.target.value = "";
          if (file) image.onUpload(file);
        }}
      />
      <div className="flex gap-1.5">
        <Button
          type="button"
          size="sm"
          variant="outline"
          className="h-8 flex-1 gap-1.5 text-[11px]"
          disabled={image.busy}
          onClick={() => fileRef.current?.click()}
          data-testid="button-panel-upload-block-image"
        >
          {image.busy ? (
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
          ) : (
            <Upload className="h-3.5 w-3.5" />
          )}
          Upload your own
        </Button>
        {image.hasSelection && (
          <Button
            type="button"
            size="sm"
            variant="ghost"
            className="h-8 gap-1.5 text-[11px]"
            disabled={image.busy}
            onClick={image.onReset}
            data-testid="button-panel-reset-block-image"
          >
            <RefreshCw className="h-3.5 w-3.5" /> Reset
          </Button>
        )}
      </div>
    </div>
  );
}

// ---- Task #2329: panel hero image picker ------------------------------------
// Current hero thumbnail + product-photo grid + upload + AI regenerate. Choose
// and upload persist through the /hero/set route (the URL becomes the canonical
// heroImageUrl for preview and send); regenerate re-rolls the AI hero worker.
// Exported for the DOM regression test (apply-lock during regen/mutations).
export function HeroImagePicker({
  image,
}: {
  image: NonNullable<EmailBlockEditPanelProps["heroImage"]>;
}) {
  const fileRef = useRef<HTMLInputElement>(null);
  const [feedback, setFeedback] = useState("");
  const anyBusy = image.busy || image.regenBusy;
  return (
    <div className="space-y-2" data-testid="panel-hero-image-picker">
      <label className="text-xs font-semibold text-foreground">Hero image</label>
      {image.currentUrl ? (
        <img
          src={image.currentUrl}
          alt="Current hero image"
          className="max-h-40 w-full rounded-md border border-border object-cover"
          data-testid="img-panel-hero-image-current"
        />
      ) : (
        <div className="rounded-md border border-dashed border-border/60 p-3 text-center text-[11px] text-muted-foreground">
          No hero image yet — choose, upload, or regenerate one.
        </div>
      )}
      {image.photos.length > 0 && (
        <>
          <p className="text-[11px] text-muted-foreground">Product photos</p>
          <div className="grid max-h-48 grid-cols-3 gap-1.5 overflow-y-auto">
            {image.photos.map((url, i) => (
              <button
                key={`${i}-${url}`}
                type="button"
                disabled={anyBusy}
                onClick={() => image.onChoose(url)}
                title="Use this product photo"
                data-testid={`button-panel-hero-image-option-${i}`}
                className={cn(
                  "relative aspect-square overflow-hidden rounded-md border bg-muted hover:border-primary disabled:opacity-40",
                  url === image.currentUrl
                    ? "border-primary ring-1 ring-primary"
                    : "border-border",
                )}
              >
                <img
                  src={url}
                  alt={`Product photo ${i + 1}`}
                  className="h-full w-full object-cover"
                />
              </button>
            ))}
          </div>
        </>
      )}
      {image.needsApply && (
        <div
          className="space-y-1.5 rounded-lg border border-amber-300/60 bg-amber-50 p-2.5 dark:border-amber-400/30 dark:bg-amber-950/30"
          data-testid="panel-hero-image-needs-apply"
        >
          <p className="text-[11px] text-amber-800 dark:text-amber-200">
            New image selected — the email still shows the old hero until you
            apply it.
          </p>
          <Button
            type="button"
            size="sm"
            className="h-8 w-full gap-1.5 text-[11px]"
            // ALSO locked while any hero mutation or AI regeneration is in
            // flight (anyBusy): applying mid-regen would polish the OLD
            // heroImageUrl and silently drop the incoming image.
            disabled={anyBusy || image.applyBusy}
            onClick={image.onApply}
            data-testid="button-panel-apply-hero-image"
          >
            {image.applyBusy ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : (
              <RefreshCw className="h-3.5 w-3.5" />
            )}
            Apply new image
          </Button>
        </div>
      )}
      <input
        ref={fileRef}
        type="file"
        accept="image/png,image/jpeg,image/webp,image/gif"
        className="hidden"
        data-testid="input-panel-upload-hero-image"
        onChange={(e) => {
          const file = e.target.files?.[0];
          e.target.value = "";
          if (file) image.onUpload(file);
        }}
      />
      <Button
        type="button"
        size="sm"
        variant="outline"
        className="h-8 w-full gap-1.5 text-[11px]"
        disabled={anyBusy}
        onClick={() => fileRef.current?.click()}
        data-testid="button-panel-upload-hero-image"
      >
        {image.busy ? (
          <Loader2 className="h-3.5 w-3.5 animate-spin" />
        ) : (
          <Upload className="h-3.5 w-3.5" />
        )}
        Upload your own
      </Button>
      <div className="space-y-1.5 rounded-lg border border-border/60 p-2.5">
        <p className="text-[11px] font-semibold text-foreground/80">
          Regenerate with AI
        </p>
        <Textarea
          rows={2}
          value={feedback}
          onChange={(e) => setFeedback(e.target.value)}
          placeholder="Optional art direction, e.g. warmer light, outdoor setting"
          className="resize-none text-xs"
          data-testid="input-panel-hero-image-feedback"
        />
        <Button
          type="button"
          size="sm"
          variant="outline"
          className="h-8 w-full gap-1.5 text-[11px]"
          disabled={anyBusy}
          onClick={() => {
            image.onRegenerate(feedback.trim());
            setFeedback("");
          }}
          data-testid="button-panel-regenerate-hero-image"
        >
          {image.regenBusy ? (
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
          ) : (
            <RefreshCw className="h-3.5 w-3.5" />
          )}
          Regenerate hero image
        </Button>
      </div>
    </div>
  );
}

export function BodyImagePicker({
  image,
}: {
  image: NonNullable<EmailBlockEditPanelProps["bodyImage"]>;
}) {
  const fileRef = useRef<HTMLInputElement>(null);
  const anyBusy = image.busy || image.applyBusy;
  const selectedUrl =
    image.preference.mode === "selected" ? image.preference.url : "";

  if (image.checkingImage) {
    return (
      <div className="space-y-2" data-testid="panel-body-image-picker">
        <label className="text-xs font-semibold text-foreground">
          Body Copy image
        </label>
        <div
          className="flex items-center justify-center gap-2 rounded-md border border-dashed border-border/60 p-3 text-center text-[11px] text-muted-foreground"
          data-testid="text-panel-body-checking-image"
        >
          <Loader2 className="h-3.5 w-3.5 animate-spin" />
          Checking the current Body Copy image…
        </div>
      </div>
    );
  }

  if (!image.hasImage) {
    return (
      <div className="space-y-2" data-testid="panel-body-image-picker">
        <label className="text-xs font-semibold text-foreground">
          Body Copy image
        </label>
        <div
          className="rounded-md border border-dashed border-border/60 p-3 text-center text-[11px] text-muted-foreground"
          data-testid="text-panel-body-has-no-image"
        >
          This Body Copy section has no image to replace.
        </div>
        {image.preference.mode !== "automatic" && (
          <Button
            type="button"
            size="sm"
            variant="ghost"
            className="h-8 w-full gap-1.5 text-[11px]"
            disabled={anyBusy}
            onClick={image.onRestoreAutomatic}
            data-testid="button-panel-restore-body-image-automatic"
          >
            <RefreshCw className="h-3.5 w-3.5" />
            Clear staged image treatment
          </Button>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-2" data-testid="panel-body-image-picker">
      <label className="text-xs font-semibold text-foreground">
        Body Copy image
      </label>
      {image.preference.mode === "selected" ? (
        <img
          src={image.preference.url}
          alt="Selected Body Copy image"
          className="h-32 w-full rounded-md border border-border object-cover"
          data-testid="img-panel-body-image-current"
        />
      ) : (
        <div
          className="rounded-md border border-dashed border-border/60 p-3 text-center text-[11px] text-muted-foreground"
          data-testid="text-panel-body-image-treatment"
        >
          {image.preference.mode === "none"
            ? "Text only — no imagery will appear in Body Copy."
            : "Automatic — keep the existing automatic Body Copy treatment."}
        </div>
      )}

      {image.photos.length > 0 && (
        <>
          <p className="text-[11px] text-muted-foreground">Product photos</p>
          <div className="grid max-h-48 grid-cols-3 gap-1.5 overflow-y-auto">
            {image.photos.map((url, index) => (
              <button
                key={`${index}-${url}`}
                type="button"
                disabled={anyBusy}
                onClick={() => image.onChoose(url)}
                title="Use this product photo"
                data-testid={`button-panel-body-image-option-${index}`}
                className={cn(
                  "relative aspect-square overflow-hidden rounded-md border bg-muted hover:border-primary disabled:opacity-40",
                  url === selectedUrl
                    ? "border-primary ring-1 ring-primary"
                    : "border-border",
                )}
              >
                <img
                  src={url}
                  alt={`Product photo ${index + 1}`}
                  className="h-full w-full object-cover"
                />
              </button>
            ))}
          </div>
        </>
      )}

      <input
        ref={fileRef}
        type="file"
        accept="image/png,image/jpeg,image/webp,image/gif"
        className="hidden"
        data-testid="input-panel-upload-body-image"
        onChange={(event) => {
          const file = event.target.files?.[0];
          event.target.value = "";
          if (file) image.onUpload(file);
        }}
      />
      <div className="grid grid-cols-2 gap-1.5">
        <Button
          type="button"
          size="sm"
          variant="outline"
          className="h-8 gap-1.5 text-[11px]"
          disabled={anyBusy}
          onClick={() => fileRef.current?.click()}
          data-testid="button-panel-upload-body-image"
        >
          {image.busy ? (
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
          ) : (
            <Upload className="h-3.5 w-3.5" />
          )}
          Upload
        </Button>
        <Button
          type="button"
          size="sm"
          variant="outline"
          className="h-8 gap-1.5 text-[11px]"
          disabled={anyBusy || image.preference.mode === "none"}
          onClick={image.onRemove}
          data-testid="button-panel-remove-body-image"
        >
          <EyeOff className="h-3.5 w-3.5" />
          Remove image
        </Button>
      </div>
      <Button
        type="button"
        size="sm"
        variant="ghost"
        className="h-8 w-full gap-1.5 text-[11px]"
        disabled={anyBusy || image.preference.mode === "automatic"}
        onClick={image.onRestoreAutomatic}
        data-testid="button-panel-restore-body-image-automatic"
      >
        <RefreshCw className="h-3.5 w-3.5" />
        Restore automatic treatment
      </Button>

      {image.needsApply && (
        <div
          className="space-y-1.5 rounded-lg border border-amber-300/60 bg-amber-50 p-2.5 dark:border-amber-400/30 dark:bg-amber-950/30"
          data-testid="panel-body-image-needs-apply"
        >
          <p className="text-[11px] text-amber-800 dark:text-amber-200">
            Image treatment saved — the email still shows the previous Body
            Copy section until you apply it.
          </p>
          <Button
            type="button"
            size="sm"
            className="h-8 w-full gap-1.5 text-[11px]"
            disabled={anyBusy}
            onClick={image.onApply}
            data-testid="button-panel-apply-body-image"
          >
            {image.applyBusy ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : (
              <RefreshCw className="h-3.5 w-3.5" />
            )}
            Apply image treatment
          </Button>
        </div>
      )}
    </div>
  );
}

// ---- Task #2556: per-slot image treatment picker for eligible body blocks ----
// Generalizes BodyImagePicker for selling_points, did_you_know, us_vs_them,
// comparison_table, and product_list. Each slot gets its own thumbnail +
// photo-grid; a single Apply banner covers all slots simultaneously.
export function BlockBodyImagePicker({
  image,
}: {
  image: NonNullable<EmailBlockEditPanelProps["blockBodyImage"]>;
}) {
  const fileRefs = useRef<Record<string, HTMLInputElement | null>>({});
  const anyBusy = image.busy || image.applyBusy;

  // Only render slots that have verified imagery — fail closed.
  const visibleSlots = image.slots.filter((s) => s.hasImage);
  if (visibleSlots.length === 0) return null;

  return (
    <div className="space-y-4" data-testid="panel-block-body-image-picker">
      {visibleSlots.map((slot) => {
        const selectedUrl =
          slot.treatment.mode === "selected" ? (slot.treatment as any).url : "";
        const isNone = slot.treatment.mode === "none";
        const isAuto = slot.treatment.mode === "automatic";
        const slotBusy = image.uploadingSlot === slot.slot ? image.busy : false;

        return (
          <div
            key={slot.slot}
            className="space-y-2"
            data-testid={`panel-block-body-image-slot-${slot.slot}`}
          >
            <label className="text-xs font-semibold text-foreground">
              {slot.label}
            </label>

            {slot.treatment.mode === "selected" ? (
              <img
                src={selectedUrl}
                alt={`Selected ${slot.label}`}
                className="h-32 w-full rounded-md border border-border object-cover"
                data-testid={`img-panel-block-body-image-current-${slot.slot}`}
              />
            ) : (
              <div
                className="rounded-md border border-dashed border-border/60 p-3 text-center text-[11px] text-muted-foreground"
                data-testid={`text-panel-block-body-image-treatment-${slot.slot}`}
              >
                {isNone
                  ? "Text only — no image for this slot."
                  : slot.currentUrl
                  ? "Automatic — using the AI-generated image."
                  : "Automatic — no image generated yet."}
              </div>
            )}

            {image.photos.length > 0 && (
              <>
                <p className="text-[11px] text-muted-foreground">
                  Product photos
                </p>
                <div className="grid max-h-48 grid-cols-3 gap-1.5 overflow-y-auto">
                  {image.photos.map((url, i) => (
                    <button
                      key={`${i}-${url}`}
                      type="button"
                      disabled={anyBusy}
                      onClick={() => image.onChoose(slot.slot, url)}
                      title="Use this product photo"
                      data-testid={`button-panel-block-body-image-option-${slot.slot}-${i}`}
                      className={cn(
                        "relative aspect-square overflow-hidden rounded-md border bg-muted hover:border-primary disabled:opacity-40",
                        url === selectedUrl
                          ? "border-primary ring-1 ring-primary"
                          : "border-border",
                      )}
                    >
                      <img
                        src={url}
                        alt={`Product photo ${i + 1}`}
                        className="h-full w-full object-cover"
                      />
                    </button>
                  ))}
                </div>
              </>
            )}

            <input
              ref={(el) => {
                fileRefs.current[slot.slot] = el;
              }}
              type="file"
              accept="image/png,image/jpeg,image/webp,image/gif"
              className="hidden"
              data-testid={`input-panel-upload-block-body-image-${slot.slot}`}
              onChange={(e) => {
                const file = e.target.files?.[0];
                e.target.value = "";
                if (file) image.onUpload(slot.slot, file);
              }}
            />

            <div
              className={cn(
                "grid gap-1.5",
                slot.allowRemove ? "grid-cols-2" : "grid-cols-1",
              )}
            >
              <Button
                type="button"
                size="sm"
                variant="outline"
                className="h-8 gap-1.5 text-[11px]"
                disabled={anyBusy}
                onClick={() => fileRefs.current[slot.slot]?.click()}
                data-testid={`button-panel-upload-block-body-image-${slot.slot}`}
              >
                {slotBusy ? (
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                ) : (
                  <Upload className="h-3.5 w-3.5" />
                )}
                Upload
              </Button>
              {/* Product List tiles cannot be image-free — Remove is omitted for
                  slots whose renderer always draws a real product photo. */}
              {slot.allowRemove && (
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  className="h-8 gap-1.5 text-[11px]"
                  disabled={anyBusy || isNone}
                  onClick={() => image.onRemove(slot.slot)}
                  data-testid={`button-panel-remove-block-body-image-${slot.slot}`}
                >
                  <EyeOff className="h-3.5 w-3.5" />
                  Remove image
                </Button>
              )}
            </div>
            <Button
              type="button"
              size="sm"
              variant="ghost"
              className="h-8 w-full gap-1.5 text-[11px]"
              disabled={anyBusy || isAuto}
              onClick={() => image.onRestoreAutomatic(slot.slot)}
              data-testid={`button-panel-restore-block-body-image-${slot.slot}`}
            >
              <RefreshCw className="h-3.5 w-3.5" />
              Restore automatic
            </Button>
          </div>
        );
      })}

      {image.needsApply && (
        <div
          className="space-y-1.5 rounded-lg border border-amber-300/60 bg-amber-50 p-2.5 dark:border-amber-400/30 dark:bg-amber-950/30"
          data-testid="panel-block-body-image-needs-apply"
        >
          <p className="text-[11px] text-amber-800 dark:text-amber-200">
            Image treatment saved — the email still shows the previous section
            until you apply it.
          </p>
          <Button
            type="button"
            size="sm"
            className="h-8 w-full gap-1.5 text-[11px]"
            disabled={anyBusy}
            onClick={image.onApply}
            data-testid="button-panel-apply-block-body-image"
          >
            {image.applyBusy ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : (
              <RefreshCw className="h-3.5 w-3.5" />
            )}
            Apply image treatment
          </Button>
        </div>
      )}
    </div>
  );
}

// ---- Per-type field sets ---------------------------------------------------

function BlockFields(props: EmailBlockEditPanelProps) {
  const {
    blockKey,
    headline,
    ctaText,
    bodyCopy,
    copy,
    punchline,
    onHeadline,
    onCtaText,
    onBodyCopy,
    onPunchline,
    onPatch,
    availableReviews,
    footer,
  } = props;
  const type = emailBlockType(blockKey);

  // Section blocks share a one-line "section headline" (punchline) + optional
  // all-caps kicker (eyebrow).
  const sectionHeading = (
    <>
      <TextField
        label="Section headline"
        hint="A short line shown above this section."
        value={punchline}
        onChange={onPunchline}
        placeholder="Optional headline"
        testid="input-block-punchline"
      />
      <TextField
        label="Eyebrow"
        hint="Small kicker above the headline (shown on some layouts)."
        value={copy.eyebrow ?? ""}
        onChange={(v) => onPatch({ eyebrow: v })}
        placeholder="Optional kicker"
        testid="input-block-eyebrow"
      />
    </>
  );

  switch (type) {
    case "hero":
      return (
        <>
          {props.heroImage && <HeroImagePicker image={props.heroImage} />}
          <TextField
            label="Headline"
            value={headline}
            onChange={onHeadline}
            placeholder="Your hero headline"
            testid="input-block-headline"
          />
          <TextField
            label="Button label"
            value={ctaText}
            onChange={onCtaText}
            placeholder="Shop now"
            testid="input-block-cta"
          />
          <TextField
            label="Eyebrow"
            hint="Small kicker above the headline (shown on some layouts)."
            value={copy.eyebrow ?? ""}
            onChange={(v) => onPatch({ eyebrow: v })}
            placeholder="Optional kicker"
            testid="input-block-eyebrow"
          />
          <TextField
            label="Pull quote"
            hint="Short highlighted line (shown on some layouts)."
            value={copy.pullQuote ?? ""}
            onChange={(v) => onPatch({ pullQuote: v })}
            placeholder="Optional pull quote"
            testid="input-block-pullquote"
          />
          <div className="space-y-2 rounded-lg border border-border/60 p-3">
            <p className="text-xs font-semibold text-foreground">
              Offer box (optional)
            </p>
            <TextField
              label="Label"
              value={copy.offerBox?.label ?? ""}
              onChange={(v) =>
                onPatch({ offerBox: { ...(copy.offerBox ?? { label: "", code: "", description: "", cta: "" }), label: v } })
              }
              placeholder="Limited offer"
              testid="input-offer-label"
            />
            <TextField
              label="Code"
              value={copy.offerBox?.code ?? ""}
              onChange={(v) =>
                onPatch({ offerBox: { ...(copy.offerBox ?? { label: "", code: "", description: "", cta: "" }), code: v } })
              }
              placeholder="SAVE20"
              testid="input-offer-code"
            />
            <TextField
              label="Description"
              value={copy.offerBox?.description ?? ""}
              onChange={(v) =>
                onPatch({ offerBox: { ...(copy.offerBox ?? { label: "", code: "", description: "", cta: "" }), description: v } })
              }
              placeholder="20% off your first order"
              testid="input-offer-description"
            />
            <TextField
              label="Button"
              value={copy.offerBox?.cta ?? ""}
              onChange={(v) =>
                onPatch({ offerBox: { ...(copy.offerBox ?? { label: "", code: "", description: "", cta: "" }), cta: v } })
              }
              placeholder="Redeem"
              testid="input-offer-cta"
            />
          </div>
        </>
      );
    case "body":
      return (
        <>
          {props.bodyImage && <BodyImagePicker image={props.bodyImage} />}
          <AreaField
            label="Body copy"
            value={bodyCopy}
            onChange={onBodyCopy}
            placeholder="Your main message…"
            rows={8}
            testid="input-block-body"
          />
          <TextField
            label="Pull quote"
            hint="A line highlighted from within the body (shown on some layouts)."
            value={copy.pullQuote ?? ""}
            onChange={(v) => onPatch({ pullQuote: v })}
            placeholder="Optional pull quote"
            testid="input-block-pullquote"
          />
        </>
      );
    case "selling_points":
      return (
        <>
          {sectionHeading}
          {props.blockBodyImage && (
            <BlockBodyImagePicker image={props.blockBodyImage} />
          )}
          <StringListField
            label="Selling points"
            items={copy.sellingPoints ?? []}
            onChange={(next) => onPatch({ sellingPoints: next })}
            placeholder="A benefit or feature"
            addLabel="Add point"
            testid="list-selling-points"
          />
        </>
      );
    case "did_you_know":
      return (
        <>
          {sectionHeading}
          {props.blockBodyImage && (
            <BlockBodyImagePicker image={props.blockBodyImage} />
          )}
          <AreaField
            label="Did you know"
            value={copy.didYouKnow ?? ""}
            onChange={(v) => onPatch({ didYouKnow: v })}
            placeholder="An interesting fact about your product…"
            rows={5}
            testid="input-did-you-know"
          />
        </>
      );
    case "logo_banner":
      return (
        <TextField
          label="Tagline"
          value={copy.logoBannerTagline ?? ""}
          onChange={(v) => onPatch({ logoBannerTagline: v })}
          placeholder="Your brand tagline"
          testid="input-logo-tagline"
        />
      );
    case "product_list":
      return (
        <>
          {sectionHeading}
          {props.blockBodyImage && (
            <BlockBodyImagePicker image={props.blockBodyImage} />
          )}
          <AreaField
            label="Intro"
            value={copy.productListIntro ?? ""}
            onChange={(v) => onPatch({ productListIntro: v })}
            placeholder="A short intro for this product grid…"
            rows={3}
            testid="input-product-list-intro"
          />
        </>
      );
    case "reviews":
      return (
        <>
          {sectionHeading}
          <ReviewsEditor
            reviews={copy.reviews ?? []}
            available={availableReviews ?? []}
            onChange={(next) => onPatch({ reviews: next })}
          />
        </>
      );
    case "us_vs_them":
      return (
        <>
          {sectionHeading}
          {props.blockBodyImage && (
            <BlockBodyImagePicker image={props.blockBodyImage} />
          )}
          <UsVsThemEditor
            rows={copy.usVsThem ?? []}
            onChange={(next) => onPatch({ usVsThem: next })}
          />
        </>
      );
    case "comparison_table":
      return (
        <>
          {sectionHeading}
          {props.blockBodyImage && (
            <BlockBodyImagePicker image={props.blockBodyImage} />
          )}
          <ComparisonTableEditor
            table={copy.comparisonTable}
            onChange={(next) => onPatch({ comparisonTable: next })}
          />
        </>
      );
    case "image_only":
      return (
        <>
          <div className="rounded-lg border border-border/60 bg-muted/30 p-3 text-xs text-muted-foreground">
            A full-width product photo with no copy. Pick one of your product's
            photos or upload your own below.
          </div>
          {props.blockImage && <BlockImagePicker image={props.blockImage} />}
        </>
      );
    case "image_discount":
      return (
        <>
          {props.blockImage && <BlockImagePicker image={props.blockImage} />}
          <TextField
            label="Note"
            hint="A short line shown on the discount band."
            value={copy.discountNote ?? ""}
            onChange={(v) => onPatch({ discountNote: v })}
            placeholder="A little something for you"
            testid="input-block-discount-note"
          />
          <TextField
            label="Discount code"
            hint="Shown in the dashed code pill. Leave blank to hide the pill."
            value={copy.discountCode ?? ""}
            onChange={(v) => onPatch({ discountCode: v })}
            placeholder="SAVE20"
            testid="input-block-discount-code"
          />
        </>
      );
    case "cta":
      return (
        <>
          <TextField
            label="Headline"
            value={copy.ctaHeadline ?? ""}
            onChange={(v) => onPatch({ ctaHeadline: v })}
            placeholder="Ready when you are"
            testid="input-block-cta-headline"
          />
          <TextField
            label="Supporting line"
            value={copy.ctaBody ?? ""}
            onChange={(v) => onPatch({ ctaBody: v })}
            placeholder="One click away."
            testid="input-block-cta-body"
          />
          <TextField
            label="Button label"
            hint="Shared with the hero button."
            value={ctaText}
            onChange={onCtaText}
            placeholder="Shop now"
            testid="input-block-cta-label"
          />
        </>
      );
    case "footer":
      return footer ? (
        <FooterEditor content={footer.content} cb={footer} />
      ) : (
        <div className="rounded-lg border border-border/60 bg-muted/30 p-3 text-xs text-muted-foreground">
          The footer is managed automatically and isn't regenerated as an image.
          Edit footer links and disclaimer text directly on the footer in the
          preview.
        </div>
      );
    default:
      return (
        <div className="text-xs text-muted-foreground">
          This block has no editable content fields.
        </div>
      );
  }
}

function ReviewsEditor({
  reviews,
  available,
  onChange,
}: {
  reviews: { quote: string; author: string }[];
  available: { quote: string; author: string; rating: number }[];
  onChange: (next: { quote: string; author: string }[]) => void;
}) {
  const set = (i: number, patch: Partial<{ quote: string; author: string }>) =>
    onChange(reviews.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));
  const remove = (i: number) => onChange(reviews.filter((_, idx) => idx !== i));
  const add = () => onChange([...reviews, { quote: "", author: "" }]);

  // Feature / un-feature a saved review by matching on its normalized quote —
  // the same identity rule the live-preview picker uses, so toggling in either
  // place stays consistent.
  const norm = (q: string) => q.trim().toLowerCase();
  const featured = new Set(reviews.map((r) => norm(r.quote)));
  const toggle = (r: { quote: string; author: string }) => {
    const k = norm(r.quote);
    if (featured.has(k)) onChange(reviews.filter((it) => norm(it.quote) !== k));
    else onChange([...reviews, { quote: r.quote, author: r.author }]);
  };

  return (
    <div className="space-y-3">
      {available.length > 0 && (
        <Field
          label="Feature saved reviews"
          hint="Pick from this product's real reviews. Selected ones appear in the block."
        >
          <div className="max-h-64 space-y-1.5 overflow-y-auto pr-0.5">
            {available.map((r, i) => {
              const isOn = featured.has(norm(r.quote));
              return (
                <button
                  key={i}
                  type="button"
                  onClick={() => toggle(r)}
                  className={cn(
                    "w-full rounded-lg border p-2.5 text-left transition-colors",
                    isOn
                      ? "border-primary bg-primary/5"
                      : "border-border/60 hover:border-border hover:bg-muted/40",
                  )}
                  data-testid={`button-pick-review-${i}`}
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-0.5">
                        {Array.from({ length: 5 }).map((_, s) => (
                          <Star
                            key={s}
                            className={cn(
                              "h-3 w-3",
                              s < r.rating
                                ? "fill-amber-400 text-amber-400"
                                : "text-muted-foreground/30",
                            )}
                          />
                        ))}
                      </div>
                      <p className="mt-1 line-clamp-2 text-[12px] leading-snug text-foreground">
                        {r.quote}
                      </p>
                      <p className="mt-0.5 text-[11px] text-muted-foreground">
                        — {r.author}
                      </p>
                    </div>
                    <span
                      className={cn(
                        "mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded-full border",
                        isOn
                          ? "border-primary bg-primary text-primary-foreground"
                          : "border-muted-foreground/40",
                      )}
                    >
                      {isOn && <Check className="h-3 w-3" />}
                    </span>
                  </div>
                </button>
              );
            })}
          </div>
        </Field>
      )}

      <Field
        label={available.length > 0 ? "Custom reviews" : "Reviews"}
        hint={
          available.length > 0
            ? "Hand-write a review that isn't in your saved list."
            : undefined
        }
      >
        <div className="space-y-2.5">
          {reviews.map((r, i) => (
            <div
              key={i}
              className="space-y-1.5 rounded-lg border border-border/60 p-2.5"
            >
              <div className="flex items-center justify-between">
                <span className="text-[11px] font-medium text-muted-foreground">
                  Review {i + 1}
                </span>
                <button
                  type="button"
                  onClick={() => remove(i)}
                  className="text-muted-foreground/60 hover:text-destructive transition-colors"
                  title="Remove review"
                  data-testid={`review-remove-${i}`}
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </div>
              <Textarea
                value={r.quote}
                onChange={(e) => set(i, { quote: e.target.value })}
                placeholder="What the customer said…"
                rows={2}
                className="text-sm resize-y"
                data-testid={`review-quote-${i}`}
              />
              <Input
                value={r.author}
                onChange={(e) => set(i, { author: e.target.value })}
                placeholder="Author"
                className="h-8 text-sm"
                data-testid={`review-author-${i}`}
              />
            </div>
          ))}
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="h-7 gap-1.5 text-xs"
            onClick={add}
            data-testid="review-add"
          >
            <Plus className="h-3 w-3" /> Add review
          </Button>
        </div>
      </Field>
    </div>
  );
}

// Footer editing surfaced in the right-hand panel — the polished view's only
// editable surface (the live inline footer editor is parked off-screen there).
// Mirrors the inline editor's capabilities: an editable (never-deletable)
// unsubscribe URL, social links (label + URL, add / remove), the Preferences /
// Contact quick links (label + URL, hide / show), and the disclaimer line.
// Each control persists a per-email override via the passed callbacks
// (autosaved like any other edit). The footer renders live — it's never a
// polished image — so edits apply to the sent email without a regenerate.
function FooterEditor({
  content,
  cb,
}: {
  content: UniversalFooterContent | null;
  cb: NonNullable<EmailBlockEditPanelProps["footer"]>;
}) {
  const f = content ?? {};
  const social: UniversalSocialLink[] = f.socialLinks ?? [];
  const setSocialPlatform = (i: number, platform: string) =>
    cb.onSetSocialLinks(
      social.map((s, idx) => (idx === i ? { ...s, platform } : s)),
    );
  const setSocialUrl = (i: number, url: string) =>
    cb.onSetSocialLinks(social.map((s, idx) => (idx === i ? { ...s, url } : s)));
  const removeSocial = (i: number) =>
    cb.onSetSocialLinks(social.filter((_, idx) => idx !== i));
  const addSocial = () =>
    cb.onSetSocialLinks([...social, { platform: "", url: "" }]);

  return (
    <div className="space-y-4">
      <p className="text-[11px] text-muted-foreground">
        These links appear in your email's footer. Changes apply to the sent
        email right away — no need to regenerate.
      </p>

      <TextField
        label="Unsubscribe link"
        hint="Leave blank to use your email platform's default unsubscribe."
        value={f.unsubscribeUrl ?? ""}
        onChange={cb.onSetUnsubscribeUrl}
        placeholder="https://…"
        testid="input-footer-unsubscribe-url"
      />

      <Field label="Social links" hint="Shown as a row of links in the footer.">
        <div className="space-y-2.5">
          {social.map((s, i) => (
            <div
              key={i}
              className="space-y-1.5 rounded-lg border border-border/60 p-2.5"
            >
              <div className="flex items-center justify-between">
                <span className="text-[11px] font-medium text-muted-foreground">
                  Link {i + 1}
                </span>
                <button
                  type="button"
                  onClick={() => removeSocial(i)}
                  className="text-muted-foreground/60 hover:text-destructive transition-colors"
                  title="Remove link"
                  data-testid={`footer-social-remove-${i}`}
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </div>
              <Input
                value={s.platform}
                onChange={(e) => setSocialPlatform(i, e.target.value)}
                placeholder="Label (e.g. Instagram)"
                className="h-8 text-sm"
                data-testid={`footer-social-label-${i}`}
              />
              <Input
                value={s.url}
                onChange={(e) => setSocialUrl(i, e.target.value)}
                placeholder="https://…"
                className="h-8 text-sm"
                data-testid={`footer-social-url-${i}`}
              />
            </div>
          ))}
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="h-7 gap-1.5 text-xs"
            onClick={addSocial}
            data-testid="footer-social-add"
          >
            <Plus className="h-3 w-3" /> Add link
          </Button>
        </div>
      </Field>

      <FooterQuickLink
        title="Preferences link"
        defaultLabel="Preferences"
        label={f.preferencesLabel ?? ""}
        url={f.preferencesUrl ?? ""}
        hidden={!!f.preferencesDeleted}
        keyId="preferences"
        onSetLabel={cb.onSetPreferencesLabel}
        onSetUrl={cb.onSetPreferencesUrl}
        onHide={cb.onDeletePreferences}
        onShow={() => cb.onSetPreferencesLabel(f.preferencesLabel ?? "")}
      />
      <FooterQuickLink
        title="Contact link"
        defaultLabel="Contact"
        label={f.contactLabel ?? ""}
        url={f.contactUrl ?? ""}
        hidden={!!f.contactDeleted}
        keyId="contact"
        onSetLabel={cb.onSetContactLabel}
        onSetUrl={cb.onSetContactUrl}
        onHide={cb.onDeleteContact}
        onShow={() => cb.onSetContactLabel(f.contactLabel ?? "")}
      />

      <FooterDisclaimer
        text={f.legalText ?? ""}
        onSet={cb.onSetDisclaimer}
        onDelete={cb.onDeleteDisclaimer}
      />
    </div>
  );
}

// A footer quick link (Preferences / Contact): label + URL editable, plus a
// hide/show toggle. Hiding sets the per-email deletion flag (link disappears on
// both the preview and the sent email); showing re-commits the current label,
// which clears that flag.
function FooterQuickLink({
  title,
  defaultLabel,
  label,
  url,
  hidden,
  keyId,
  onSetLabel,
  onSetUrl,
  onHide,
  onShow,
}: {
  title: string;
  defaultLabel: string;
  label: string;
  url: string;
  hidden: boolean;
  keyId: string;
  onSetLabel: (v: string) => void;
  onSetUrl: (v: string) => void;
  onHide: () => void;
  onShow: () => void;
}) {
  return (
    <Field label={title}>
      {hidden ? (
        <div className="flex items-center justify-between rounded-lg border border-dashed border-border/60 px-2.5 py-2 text-[11px] text-muted-foreground">
          <span className="inline-flex items-center gap-1.5">
            <EyeOff className="h-3.5 w-3.5" /> Hidden
          </span>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="h-6 gap-1 text-xs"
            onClick={onShow}
            data-testid={`button-footer-show-${keyId}`}
          >
            <Eye className="h-3 w-3" /> Show
          </Button>
        </div>
      ) : (
        <div className="space-y-1.5 rounded-lg border border-border/60 p-2.5">
          <Input
            value={label}
            onChange={(e) => onSetLabel(e.target.value)}
            placeholder={defaultLabel}
            className="h-8 text-sm"
            data-testid={`input-footer-${keyId}-label`}
          />
          <Input
            value={url}
            onChange={(e) => onSetUrl(e.target.value)}
            placeholder="https://…"
            className="h-8 text-sm"
            data-testid={`input-footer-${keyId}-url`}
          />
          <button
            type="button"
            onClick={onHide}
            className="inline-flex items-center gap-1 text-[11px] text-muted-foreground/70 hover:text-destructive transition-colors"
            data-testid={`button-footer-hide-${keyId}`}
          >
            <EyeOff className="h-3 w-3" /> Hide this link
          </button>
        </div>
      )}
    </Field>
  );
}

// The footer disclaimer (legal line): editable text + a hide affordance. A blank
// value hides it on both surfaces, so hiding simply clears the override text.
function FooterDisclaimer({
  text,
  onSet,
  onDelete,
}: {
  text: string;
  onSet: (v: string) => void;
  onDelete: () => void;
}) {
  return (
    <Field label="Disclaimer">
      <Textarea
        value={text}
        onChange={(e) => onSet(e.target.value)}
        placeholder="Legal / disclaimer line shown at the bottom of the footer…"
        rows={3}
        className="text-sm resize-y"
        data-testid="input-footer-disclaimer"
      />
      {!!text.trim() && (
        <button
          type="button"
          onClick={onDelete}
          className="mt-1.5 inline-flex items-center gap-1 text-[11px] text-muted-foreground/70 hover:text-destructive transition-colors"
          data-testid="button-footer-hide-disclaimer"
        >
          <EyeOff className="h-3 w-3" /> Hide disclaimer
        </button>
      )}
    </Field>
  );
}

function UsVsThemEditor({
  rows,
  onChange,
}: {
  rows: { us: string; them: string }[];
  onChange: (next: { us: string; them: string }[]) => void;
}) {
  const set = (i: number, patch: Partial<{ us: string; them: string }>) =>
    onChange(rows.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));
  const remove = (i: number) => onChange(rows.filter((_, idx) => idx !== i));
  const add = () => onChange([...rows, { us: "", them: "" }]);
  return (
    <Field label="Us vs. them">
      <div className="space-y-2.5">
        {rows.map((r, i) => (
          <div
            key={i}
            className="space-y-1.5 rounded-lg border border-border/60 p-2.5"
          >
            <div className="flex items-center justify-between">
              <span className="text-[11px] font-medium text-muted-foreground">
                Row {i + 1}
              </span>
              <button
                type="button"
                onClick={() => remove(i)}
                className="text-muted-foreground/60 hover:text-destructive transition-colors"
                title="Remove row"
                data-testid={`uvt-remove-${i}`}
              >
                <Trash2 className="h-3.5 w-3.5" />
              </button>
            </div>
            <Input
              value={r.us}
              onChange={(e) => set(i, { us: e.target.value })}
              placeholder="Us — what you offer"
              className="h-8 text-sm"
              data-testid={`uvt-us-${i}`}
            />
            <Input
              value={r.them}
              onChange={(e) => set(i, { them: e.target.value })}
              placeholder="Them — the alternative"
              className="h-8 text-sm"
              data-testid={`uvt-them-${i}`}
            />
          </div>
        ))}
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="h-7 gap-1.5 text-xs"
          onClick={add}
          data-testid="uvt-add"
        >
          <Plus className="h-3 w-3" /> Add row
        </Button>
      </div>
    </Field>
  );
}

function ComparisonTableEditor({
  table,
  onChange,
}: {
  table?: { columns: string[]; rows: { feature: string; values: string[] }[] };
  onChange: (next: {
    columns: string[];
    rows: { feature: string; values: string[] }[];
  }) => void;
}) {
  const columns = table?.columns ?? [];
  const rows = table?.rows ?? [];
  const setColumn = (i: number, v: string) =>
    onChange({ columns: columns.map((c, idx) => (idx === i ? v : c)), rows });
  const setFeature = (ri: number, v: string) =>
    onChange({
      columns,
      rows: rows.map((r, idx) => (idx === ri ? { ...r, feature: v } : r)),
    });
  const setValue = (ri: number, ci: number, v: string) =>
    onChange({
      columns,
      rows: rows.map((r, idx) =>
        idx === ri
          ? { ...r, values: r.values.map((x, j) => (j === ci ? v : x)) }
          : r,
      ),
    });
  const addRow = () =>
    onChange({
      columns,
      rows: [
        ...rows,
        { feature: "", values: columns.map(() => "") },
      ],
    });
  const removeRow = (ri: number) =>
    onChange({ columns, rows: rows.filter((_, idx) => idx !== ri) });

  return (
    <Field
      label="Comparison table"
      hint="First column is your brand. Use short values like ✓ / ✗ or a word."
    >
      <div className="space-y-2.5">
        <div className="space-y-1.5">
          <span className="text-[11px] font-medium text-muted-foreground">
            Columns
          </span>
          {columns.map((c, i) => (
            <Input
              key={i}
              value={c}
              onChange={(e) => setColumn(i, e.target.value)}
              placeholder={i === 0 ? "Your brand" : "Alternative"}
              className="h-8 text-sm"
              data-testid={`ct-column-${i}`}
            />
          ))}
        </div>
        {rows.map((r, ri) => (
          <div
            key={ri}
            className="space-y-1.5 rounded-lg border border-border/60 p-2.5"
          >
            <div className="flex items-center justify-between">
              <span className="text-[11px] font-medium text-muted-foreground">
                Feature {ri + 1}
              </span>
              <button
                type="button"
                onClick={() => removeRow(ri)}
                className="text-muted-foreground/60 hover:text-destructive transition-colors"
                title="Remove feature"
                data-testid={`ct-remove-${ri}`}
              >
                <Trash2 className="h-3.5 w-3.5" />
              </button>
            </div>
            <Input
              value={r.feature}
              onChange={(e) => setFeature(ri, e.target.value)}
              placeholder="Feature"
              className="h-8 text-sm"
              data-testid={`ct-feature-${ri}`}
            />
            <div className="grid grid-cols-2 gap-1.5">
              {r.values.map((v, ci) => (
                <Input
                  key={ci}
                  value={v}
                  onChange={(e) => setValue(ri, ci, e.target.value)}
                  placeholder={columns[ci] || `Col ${ci + 1}`}
                  className="h-8 text-sm"
                  data-testid={`ct-value-${ri}-${ci}`}
                />
              ))}
            </div>
          </div>
        ))}
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="h-7 gap-1.5 text-xs"
          onClick={addRow}
          data-testid="ct-add-row"
        >
          <Plus className="h-3 w-3" /> Add feature
        </Button>
      </div>
    </Field>
  );
}

// ---- Panel shell -----------------------------------------------------------

const SECTION_SCOPE_LABELS: Record<EmailSectionRegenScope, string> = {
  copy: "Copy",
  layout: "Layout",
  image: "Image",
  full: "Everything",
};

export function EmailBlockEditPanel(props: EmailBlockEditPanelProps) {
  const {
    label,
    state,
    canRegenerate,
    errorMessage,
    onRegenerate,
    onClose,
    blockKey,
    isLocked,
    isEdited,
    lockBusy,
    clearBusy,
    sectionBusy,
    onToggleLock,
    onClearEdits,
    onRegenerateSection,
  } = props;
  const isFooter = emailBlockType(blockKey) === "footer";
  const isHero = emailBlockType(blockKey) === "hero";
  // Image blocks (Task #2286) carry NO AI copy — the copy-regen route rejects
  // them ("Invalid block"), so the scoped "Regenerate this section" controls
  // are hidden; the image picker above is the way to change them, and the
  // primary Apply-changes button still re-renders the polished tile.
  const isImageBlock =
    emailBlockType(blockKey) === "image_only" ||
    emailBlockType(blockKey) === "image_discount";
  // Optional free-text feedback for the scoped "Regenerate this section"
  // action below. Sent with the selected scope when the primary Regenerate
  // button is clicked (blank = same behavior as before), then cleared once
  // the regen kicks off.
  const [sectionFeedback, setSectionFeedback] = useState("");
  // Which part of the section the primary Regenerate button targets. Defaults
  // to "full" (everything) so typing feedback + clicking Regenerate always
  // does the intuitive thing without an extra choice.
  const [sectionScope, setSectionScope] =
    useState<EmailSectionRegenScope>("full");

  const stateBadge =
    state === "regenerating" ? (
      <span
        className="inline-flex items-center gap-1 text-[11px] font-medium text-primary"
        data-testid="block-state-regenerating"
      >
        <Loader2 className="h-3 w-3 animate-spin" /> Regenerating…
      </span>
    ) : state === "failed" ? (
      <span
        className="inline-flex items-center gap-1 text-[11px] font-medium text-destructive"
        data-testid="block-state-failed"
      >
        <AlertCircle className="h-3 w-3" /> Couldn't regenerate
      </span>
    ) : state === "pending" ? (
      <span
        className="inline-flex items-center gap-1 text-[11px] font-medium text-amber-600"
        data-testid="block-state-pending"
      >
        <span className="h-2 w-2 rounded-full bg-amber-500" /> Pending changes
      </span>
    ) : (
      <span
        className="inline-flex items-center gap-1 text-[11px] font-medium text-emerald-600"
        data-testid="block-state-polished"
      >
        <CheckCircle2 className="h-3 w-3" /> Polished
      </span>
    );

  return (
    <aside
      className="w-[320px] shrink-0 border-l bg-background overflow-y-auto min-h-0 flex flex-col hidden lg:flex"
      data-testid="panel-block-edit"
    >
      <div className="flex items-center justify-between gap-2 border-b px-4 py-3">
        <div className="min-w-0">
          <p className="text-sm font-semibold truncate" data-testid="text-edit-block-label">
            {label}
          </p>
          <div className="mt-0.5">{stateBadge}</div>
        </div>
        <button
          type="button"
          onClick={onClose}
          className="text-muted-foreground hover:text-foreground transition-colors shrink-0"
          title="Close panel"
          data-testid="button-close-edit-panel"
        >
          <PanelRightClose className="h-4 w-4" />
        </button>
      </div>

      <div className="flex-1 space-y-4 px-4 py-4">
        <BlockFields {...props} />
        {props.ctaLink && (
          <div className="border-t pt-4">
            <CtaLinkField
              defaultUrl={props.ctaLink.defaultUrl}
              overrideUrl={props.ctaLink.overrideUrl}
              onChange={props.ctaLink.onChange}
            />
          </div>
        )}
      </div>

      {!isFooter && (
        <div className="sticky bottom-0 border-t bg-background px-4 py-3">
          {/* Task #863: lock + hand-edit status for this section. */}
          {(onToggleLock || (isEdited && onClearEdits)) && (
            <div className="mb-3 space-y-2 rounded-md border bg-muted/30 p-2.5">
              {onToggleLock && (
                <div className="flex items-center justify-between gap-2">
                  <span className="flex items-center gap-1.5 text-[11px] font-medium text-muted-foreground">
                    {isLocked ? (
                      <Lock className="h-3 w-3" />
                    ) : (
                      <Unlock className="h-3 w-3" />
                    )}
                    {isLocked ? "Locked" : "Unlocked"}
                  </span>
                  <Button
                    size="sm"
                    variant="ghost"
                    className="h-7 gap-1.5 px-2 text-[11px]"
                    disabled={lockBusy}
                    onClick={() => onToggleLock(!isLocked)}
                    data-testid="button-toggle-lock-section"
                  >
                    {lockBusy ? (
                      <Loader2 className="h-3 w-3 animate-spin" />
                    ) : isLocked ? (
                      <Unlock className="h-3 w-3" />
                    ) : (
                      <Lock className="h-3 w-3" />
                    )}
                    {isLocked ? "Unlock" : "Lock"}
                  </Button>
                </div>
              )}
              {isEdited && onClearEdits && (
                <div className="flex items-center justify-between gap-2">
                  <span className="text-[11px] text-muted-foreground">
                    Hand-edited — preserved on regenerate.
                  </span>
                  <Button
                    size="sm"
                    variant="ghost"
                    className="h-7 gap-1.5 px-2 text-[11px]"
                    disabled={clearBusy}
                    onClick={onClearEdits}
                    data-testid="button-clear-edits-section"
                  >
                    {clearBusy ? (
                      <Loader2 className="h-3 w-3 animate-spin" />
                    ) : (
                      <RefreshCw className="h-3 w-3" />
                    )}
                    Clear edits
                  </Button>
                </div>
              )}
            </div>
          )}

          {/* Task #863: scoped "Regenerate this section" — copy / layout / image
              / everything. Locked/edited sections are refused server-side (409). */}
          {onRegenerateSection && !isImageBlock && (
            <div className="mb-3">
              <p className="mb-1.5 text-[11px] font-semibold text-foreground">
                Regenerate this section
              </p>
              <Textarea
                rows={2}
                value={sectionFeedback}
                onChange={(e) => setSectionFeedback(e.target.value)}
                placeholder="Optional feedback (e.g. shorter, focus on benefits)"
                className="mb-1.5 min-h-0 resize-none text-[11px]"
                data-testid="input-feedback-regen-section"
              />
              <p className="mb-1 text-[11px] text-muted-foreground">
                What to regenerate
              </p>
              <div className="mb-1.5 grid grid-cols-2 gap-1.5" role="radiogroup" aria-label="What to regenerate">
                {EMAIL_SECTION_REGEN_SCOPES.filter(
                  // Hero re-composition already covers the whole hero; per-scope
                  // "layout" is meaningful for section blocks only.
                  (scope) => !(isHero && scope === "layout"),
                ).map((scope) => {
                  const selected = sectionScope === scope;
                  return (
                    <Button
                      key={scope}
                      type="button"
                      size="sm"
                      variant={selected ? "secondary" : "outline"}
                      role="radio"
                      aria-checked={selected}
                      className={cn(
                        "h-8 gap-1.5 text-[11px]",
                        selected && "border border-primary/60 font-semibold",
                      )}
                      disabled={sectionBusy}
                      onClick={() => setSectionScope(scope)}
                      data-testid={`button-regen-section-${scope}`}
                    >
                      {selected && <Check className="h-3 w-3" />}
                      {SECTION_SCOPE_LABELS[scope]}
                    </Button>
                  );
                })}
              </div>
              <Button
                type="button"
                size="sm"
                className="w-full gap-1.5"
                disabled={sectionBusy}
                onClick={() => {
                  onRegenerateSection(
                    // Hero has no "layout" scope; if it was somehow selected,
                    // fall back to regenerating everything.
                    isHero && sectionScope === "layout" ? "full" : sectionScope,
                    sectionFeedback.trim() || undefined,
                  );
                  setSectionFeedback("");
                }}
                data-testid="button-regenerate-section-submit"
              >
                {sectionBusy ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <RefreshCw className="h-4 w-4" />
                )}
                {sectionBusy
                  ? "Regenerating…"
                  : `Regenerate ${SECTION_SCOPE_LABELS[
                      isHero && sectionScope === "layout" ? "full" : sectionScope
                    ].toLowerCase()}`}
              </Button>
            </div>
          )}

          {!canRegenerate && state === "pending" ? (
            <p className="mb-2 text-[11px] text-muted-foreground">
              This email predates per-section regeneration — your edits apply on
              the next full re-polish.
            </p>
          ) : null}
          {/* Task #974: when the scoped-regen area (and its shared feedback
              textarea) isn't rendered, the primary Apply-changes / Retry button
              still gets its own optional feedback input while actionable. */}
          {!onRegenerateSection &&
          (state === "pending" || state === "failed") ? (
            <Textarea
              rows={2}
              value={sectionFeedback}
              onChange={(e) => setSectionFeedback(e.target.value)}
              placeholder="Optional feedback (e.g. shorter, focus on benefits)"
              className="mb-1.5 min-h-0 resize-none text-[11px]"
              data-testid="input-feedback-regen-section"
            />
          ) : null}
          {state === "failed" ? (
            <p
              className="mb-2 flex items-start gap-1.5 text-[11px] text-destructive"
              data-testid="text-block-error"
            >
              <AlertCircle className="mt-px h-3 w-3 shrink-0" />
              <span>
                {errorMessage
                  ? `This section couldn't be regenerated: ${errorMessage}`
                  : "This section couldn't be regenerated. Your previous image is still shown — try again."}
              </span>
            </p>
          ) : null}
          {/* When the scoped Regenerate area is shown above, this button only
              appears while it's actually actionable (pending edits / failed /
              in flight) — a permanently disabled "Up to date" button next to a
              feedback box read as a broken regenerate control. Without the
              scoped area (legacy emails) it stays as the panel's only action,
              with its original state-labelled behavior. */}
          {(!onRegenerateSection || isImageBlock || state !== "polished") && (
            <Button
              size="sm"
              variant={state === "failed" ? "destructive" : "default"}
              className="w-full gap-1.5"
              disabled={state !== "pending" && state !== "failed"}
              onClick={() => {
                onRegenerate(sectionFeedback.trim() || undefined);
                setSectionFeedback("");
              }}
              data-testid="button-regenerate-block"
            >
              {state === "regenerating" ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : state === "failed" ? (
                <RefreshCw className="h-4 w-4" />
              ) : state === "pending" ? (
                <RefreshCw className="h-4 w-4" />
              ) : (
                <CheckCircle2 className="h-4 w-4" />
              )}
              {state === "regenerating"
                ? "Regenerating…"
                : state === "failed"
                  ? "Retry this section"
                  : state === "pending"
                    ? "Apply my edits"
                    : "Up to date"}
            </Button>
          )}
        </div>
      )}
      {isFooter && (
        <div className="sticky bottom-0 border-t bg-background px-4 py-3">
          {/* The footer renders deterministically from the email's current
              design spec, so a refresh is enough to bring it back in line with
              the rest of the email after other sections changed. */}
          {state === "failed" ? (
            <p
              className="mb-2 flex items-start gap-1.5 text-[11px] text-destructive"
              data-testid="text-block-error"
            >
              <AlertCircle className="mt-px h-3 w-3 shrink-0" />
              <span>
                {errorMessage
                  ? `The footer couldn't be refreshed: ${errorMessage}`
                  : "The footer couldn't be refreshed. Your previous image is still shown — try again."}
              </span>
            </p>
          ) : (
            <p className="mb-2 text-[11px] text-muted-foreground">
              {!canRegenerate
                ? "Waiting for the current run to finish — refresh will be available after."
                : "Refresh re-renders the footer to match the email's current look."}
            </p>
          )}
          <Button
            size="sm"
            variant={state === "failed" ? "destructive" : "outline"}
            className="w-full gap-1.5"
            disabled={!canRegenerate || state === "regenerating"}
            onClick={() => onRegenerate(undefined)}
            data-testid="button-refresh-footer"
          >
            {state === "regenerating" ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <RefreshCw className="h-4 w-4" />
            )}
            {state === "regenerating"
              ? "Refreshing…"
              : state === "failed"
                ? "Retry footer refresh"
                : "Refresh footer"}
          </Button>
        </div>
      )}
    </aside>
  );
}
